import { useEffect, useState } from "react";
import type { MediaDetail, MediaListItem } from "@atlas/contracts";
import { addMediaProgress, ApiError, deleteMedia, fetchMedia, fetchMediaItem, updateMedia } from "../../lib/api";
import { statusLabel } from "../../lib/statusLabels";
import { statusTone } from "../../lib/statusTone";
import { formatNumberFa } from "../../lib/format";
import { AppLink } from "../AppLink";
import { AddMediaSheet } from "../AddMediaSheet";
import { DomainListPage } from "../DomainListPage";
import { DomainDetailPage } from "../DomainDetailPage";
import { DetailActionMenu } from "../DetailActionMenu";
import { RatingBadge, RatingDot } from "../RatingBadge";
import { Badge } from "../ui/Badge";
import { Sheet } from "../ui/Sheet";
import { AlertDialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Field, Input, Textarea } from "../ui/Input";
import { ChipGroup } from "../ui/Select";
import { useToast } from "../ui/Toast";
import { Icon } from "../Icon";

const PAGE_SIZE = 20;
const MEDIA_STATUSES: Array<{ value: "" | "want" | "watch" | "finished" | "dropped"; label: string }> = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "want", label: "می‌خواهم ببینم" },
  { value: "watch", label: "در حال تماشا" },
  { value: "finished", label: "تمام‌شده" },
  { value: "dropped", label: "رهاشده" },
];
const MEDIA_TYPES: Array<{ value: "" | "movie" | "series"; label: string }> = [
  { value: "", label: "همه نوع‌ها" },
  { value: "movie", label: "فیلم" },
  { value: "series", label: "سریال" },
];

interface MediaListPageProps {
  onNavigate: (path: string) => void;
  onOpenSearch: () => void;
  /** Set by the Quick Add flow to jump straight into the add-media sheet. */
  autoOpenAdd?: boolean;
  onAutoOpenHandled?: () => void;
}

export function MediaListPage({ onNavigate, onOpenSearch, autoOpenAdd, onAutoOpenHandled }: MediaListPageProps) {
  const [status, setStatus] = useState<"" | "want" | "watch" | "finished" | "dropped">("");
  const [type, setType] = useState<"" | "movie" | "series">("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<MediaListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchMedia(
      { status: status || undefined, type: type || undefined, q: query.trim() || undefined, limit: PAGE_SIZE, offset: 0 },
      controller.signal,
    )
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setItems(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری فیلم و سریال با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, type, query, reloadToken]);

  useEffect(() => {
    if (!autoOpenAdd) return;
    setAddOpen(true);
    onAutoOpenHandled?.();
    // Only re-run when the Quick Add flow flips the trigger on; reading the
    // latest onAutoOpenHandled via closure is fine since this effect always
    // consumes and clears the trigger immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd]);

  function handleMediaCreated(media: MediaDetail) {
    setAddOpen(false);
    setReloadToken((t) => t + 1);
    onNavigate(`/media/${media.slug}`);
  }

  const statusText = MEDIA_STATUSES.find((s) => s.value === status)?.label ?? "همه وضعیت‌ها";
  const typeText = MEDIA_TYPES.find((t) => t.value === type)?.label ?? "همه نوع‌ها";
  const filterSummary = `${typeText} · ${statusText} · ${formatNumberFa(total)} مورد`;

  return (
    <>
      <DomainListPage
        title="فیلم و سریال"
        onOpenSearch={onOpenSearch}
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="جست‌وجو در فیلم و سریال"
        onOpenFilterSheet={() => setFilterOpen(true)}
        filterActive={status !== "" || type !== ""}
        filterSummary={filterSummary}
        items={items}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        emptyTitle="چیزی با این فیلتر پیدا نشد"
        emptyHint="فیلتر را تغییر بده یا پاک کن"
        getKey={(item) => item.id}
        renderItem={(item) => (
          <AppLink href={`/media/${item.slug}`} onNavigate={onNavigate} className="item-row">
            {item.assetUrl ? (
              <img src={item.assetUrl} alt={item.titleFa} className="item-row__media item-row__media--media" />
            ) : (
              <span className="item-row__media item-row__media--media">پوستر</span>
            )}
            <span className="item-row__body">
              <span className="item-row__top">
                <span className="item-row__title">{item.titleFa}</span>
                <span className="badge badge--outline">{item.type === "movie" ? "فیلم" : "سریال"}</span>
              </span>
              <span className="item-row__meta">
                <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
                <RatingDot rating={item.rating} />
              </span>
            </span>
          </AppLink>
        )}
      />
      <Sheet open={filterOpen} onClose={() => setFilterOpen(false)} title="فیلتر و مرتب‌سازی فیلم و سریال">
        <div className="filter-group">
          <p className="filter-group__label">نوع</p>
          <ChipGroup options={MEDIA_TYPES} value={type} onChange={setType} ariaLabel="نوع" />
        </div>
        <div className="filter-group">
          <p className="filter-group__label">وضعیت</p>
          <ChipGroup options={MEDIA_STATUSES} value={status} onChange={setStatus} ariaLabel="وضعیت" />
        </div>
        <div className="sheet-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setStatus("");
              setType("");
            }}
          >
            پاک‌کردن فیلترها
          </Button>
          <Button variant="primary" onClick={() => setFilterOpen(false)}>
            اعمال فیلتر
          </Button>
        </div>
      </Sheet>
      <AddMediaSheet open={addOpen} onClose={() => setAddOpen(false)} onCreated={handleMediaCreated} />
    </>
  );
}

interface MediaDetailPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

type ActiveSheet = "edit" | "status" | "rating" | "progress" | null;

export function MediaDetailPage({ slug, onNavigate }: MediaDetailPageProps) {
  const [item, setItem] = useState<MediaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const [formTitleFa, setFormTitleFa] = useState("");
  const [formTitleEn, setFormTitleEn] = useState("");
  const [formYear, setFormYear] = useState("");
  const [formStatus, setFormStatus] = useState<"want" | "watch" | "finished" | "dropped">("want");
  const [formRating, setFormRating] = useState("");
  const [formSeason, setFormSeason] = useState("");
  const [formEpisode, setFormEpisode] = useState("");
  const [formNote, setFormNote] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchMediaItem(slug, controller.signal)
      .then(setItem)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setItem(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, reloadToken]);

  function openEdit() {
    if (!item) return;
    setFormTitleFa(item.titleFa);
    setFormTitleEn(item.titleEn ?? "");
    setFormYear(item.year ?? "");
    setActiveSheet("edit");
  }

  function openStatus() {
    if (!item) return;
    setFormStatus(item.status);
    setActiveSheet("status");
  }

  function openRating() {
    if (!item) return;
    setFormRating(item.rating !== undefined ? String(item.rating) : "");
    setActiveSheet("rating");
  }

  function openProgress() {
    if (!item) return;
    setFormSeason(item.season !== undefined ? String(item.season) : "");
    setFormEpisode(item.episode !== undefined ? String(item.episode) : "");
    setFormNote("");
    setActiveSheet("progress");
  }

  async function submitStatus() {
    if (!item) return;
    setBusy(true);
    try {
      const updated = await updateMedia(item.slug, { status: formStatus });
      setItem(updated);
      setActiveSheet(null);
      toast.push("وضعیت به‌روزرسانی شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "تغییر وضعیت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitRating() {
    if (!item) return;
    const rating = Number(formRating);
    if (!Number.isInteger(rating) || rating < 0 || rating > 10) return;
    setBusy(true);
    try {
      const updated = await updateMedia(item.slug, { rating });
      setItem(updated);
      setActiveSheet(null);
      toast.push("امتیاز شخصی ثبت شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت امتیاز با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitProgress() {
    if (!item) return;
    setBusy(true);
    try {
      const season = formSeason.trim() ? Number(formSeason) : undefined;
      const episode = formEpisode.trim() ? Number(formEpisode) : undefined;
      const updated = await addMediaProgress(item.slug, { season, episode, note: formNote.trim() || undefined });
      setItem(updated);
      setActiveSheet(null);
      toast.push("پیشرفت ثبت شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت پیشرفت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function nextEpisode() {
    if (!item || typeof item.episode !== "number") return;
    setBusy(true);
    try {
      const updated = await addMediaProgress(item.slug, { season: item.season, episode: item.episode + 1 });
      setItem(updated);
      toast.push("قسمت بعد ثبت شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت قسمت بعد با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function markFinished() {
    if (!item) return;
    setBusy(true);
    try {
      const updated = await updateMedia(item.slug, { status: "finished" });
      setItem(updated);
      toast.push("به‌عنوان تمام‌شده علامت‌گذاری شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "خطا در ثبت.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndDelete() {
    if (!item) return;
    setBusy(true);
    try {
      await deleteMedia(item.slug);
      toast.push("مورد حذف شد.");
      onNavigate("/media");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "حذف با خطا مواجه شد.", "danger");
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <>
      <DomainDetailPage
        backHref="/media"
        backLabel="بازگشت"
        onNavigate={onNavigate}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        hero={item?.assetUrl ? (
          <img src={item.assetUrl} alt={item?.titleFa} className="detail-hero__cover detail-hero__cover--media" />
        ) : (
          <span className="detail-hero__cover detail-hero__cover--media">پوستر فیلم</span>
        )}
      >
        {item && (
          <div style={{ padding: "0 1.1rem" }}>
            <h1 className="detail-title">
              {item.titleFa}{" "}
              <span className="badge badge--outline" style={{ marginRight: 6 }}>
                {item.type === "movie" ? "فیلم" : "سریال"}
              </span>
            </h1>
            {item.titleEn && <p className="detail-subtitle">{item.titleEn}</p>}

            <div className="detail-badges">
              <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
              <RatingBadge rating={item.rating} />
            </div>

            <div className="info-card">
              {item.year && (
                <div className="finance-tx-row" style={{ marginBottom: 8, background: "transparent", border: "none", padding: 0 }}>
                  <span style={{ color: "var(--text-muted)" }}>سال</span>
                  <span>{item.year}</span>
                </div>
              )}
              {item.type === "series" && typeof item.season === "number" && (
                <div className="finance-tx-row" style={{ marginBottom: 8, background: "transparent", border: "none", padding: 0 }}>
                  <span style={{ color: "var(--text-muted)" }}>پیشرفت</span>
                  <span>
                    فصل {formatNumberFa(item.season)} · قسمت {formatNumberFa(item.episode ?? 0)}
                  </span>
                </div>
              )}
              <div className="info-card__actions">
                <button type="button" className="info-card__action" onClick={openRating}>
                  ثبت امتیاز
                </button>
                <button type="button" className="info-card__action" onClick={markFinished}>
                  علامت به‌عنوان تمام‌شده
                </button>
              </div>
              {item.type === "series" && (
                <div className="info-card__actions" style={{ marginTop: 8 }}>
                  <button type="button" className="info-card__action" onClick={nextEpisode}>
                    قسمت بعد ›
                  </button>
                  <button type="button" className="info-card__action" onClick={openProgress}>
                    ثبت پیشرفت
                  </button>
                </div>
              )}
            </div>

            {item.genres.length > 0 && (
              <div className="section">
                <p className="section__label">
                  <Icon name="tag" width={13} height={13} aria-hidden="true" />
                  ژانر
                </p>
                <div className="chip-list">
                  {item.genres.map((g) => (
                    <span key={g} className="chip">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {item.actors.length > 0 && (
              <div className="section">
                <p className="section__label">
                  <Icon name="users" width={13} height={13} aria-hidden="true" />
                  افراد
                </p>
                <div className="people-list">
                  {item.actors.map((a, i) => (
                    <div key={`${a.name}-${i}`} className="person-row">
                      {a.portraitUrl ? (
                        <img src={a.portraitUrl} alt={a.name} className="person-row__avatar" />
                      ) : (
                        <span className="person-row__avatar" aria-hidden="true">
                          چهره
                        </span>
                      )}
                      <div>
                        <p className="person-row__name">{a.name}</p>
                        {a.role && <p className="person-row__role">{a.role}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {item.notes && (
              <div className="section">
                <p className="section__label">
                  <Icon name="notebook" width={13} height={13} aria-hidden="true" />
                  یادداشت
                </p>
                <p className="notes-box">{item.notes}</p>
              </div>
            )}
          </div>
        )}
      </DomainDetailPage>

      {item && (
        <DetailActionMenu
          actions={[
            { label: "ویرایش", icon: "notebook", onClick: openEdit },
            { label: "تغییر وضعیت", icon: "check", onClick: openStatus },
            { label: "افزودن یادداشت", icon: "notebook", onClick: openEdit },
            { label: "حذف", icon: "x", onClick: () => setConfirmDelete(true), danger: true },
          ]}
        />
      )}

      <Sheet open={activeSheet === "edit"} onClose={() => setActiveSheet(null)} title="ویرایش">
        <Field label="عنوان">
          <Input value={formTitleFa} onChange={(e) => setFormTitleFa(e.target.value)} />
        </Field>
        <Field label="عنوان انگلیسی">
          <Input value={formTitleEn} onChange={(e) => setFormTitleEn(e.target.value)} />
        </Field>
        <Field label="سال">
          <Input value={formYear} onChange={(e) => setFormYear(e.target.value)} dir="ltr" />
        </Field>
        <Field label="یادداشت">
          <Textarea rows={3} placeholder="یادداشت را اینجا بنویس" value={formNote} onChange={(e) => setFormNote(e.target.value)} />
        </Field>
        <Button
          variant="primary"
          block
          disabled={busy}
          style={{ marginTop: 14 }}
          onClick={async () => {
            if (!item) return;
            setBusy(true);
            try {
              const updated = await updateMedia(item.slug, {
                titleFa: formTitleFa.trim() || undefined,
                titleEn: formTitleEn,
                year: formYear,
                notes: formNote,
              });
              setItem(updated);
              setActiveSheet(null);
              toast.push("تغییرات ذخیره شد.");
            } catch (err) {
              toast.push(err instanceof ApiError ? err.message : "ذخیره با خطا مواجه شد.", "danger");
            } finally {
              setBusy(false);
            }
          }}
        >
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "status"} onClose={() => setActiveSheet(null)} title="تغییر وضعیت">
        <ChipGroup
          options={[
            { label: "می‌خواهم ببینم", value: "want" },
            { label: "در حال تماشا", value: "watch" },
            { label: "تمام‌شده", value: "finished" },
            { label: "رهاشده", value: "dropped" },
          ]}
          value={formStatus}
          onChange={(v) => setFormStatus(v as typeof formStatus)}
          ariaLabel="وضعیت"
        />
        <Button variant="primary" block onClick={submitStatus} disabled={busy} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "rating"} onClose={() => setActiveSheet(null)} title="ثبت امتیاز شخصی">
        <Field label="امتیاز شخصی (۰ تا ۱۰)">
          <Input type="number" dir="ltr" min={0} max={10} value={formRating} onChange={(e) => setFormRating(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitRating} disabled={busy} style={{ marginTop: 14 }}>
          ثبت
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "progress"} onClose={() => setActiveSheet(null)} title="ثبت پیشرفت">
        <Field label="فصل">
          <Input type="number" dir="ltr" value={formSeason} onChange={(e) => setFormSeason(e.target.value)} />
        </Field>
        <Field label="قسمت">
          <Input type="number" dir="ltr" value={formEpisode} onChange={(e) => setFormEpisode(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitProgress} disabled={busy} style={{ marginTop: 14 }}>
          ثبت
        </Button>
      </Sheet>

      <AlertDialog
        open={confirmDelete}
        title="حذف"
        message={`آیا از حذف «${item?.titleFa ?? ""}» مطمئن هستید؟ این کار قابل بازگشت نیست.`}
        confirmLabel="حذف"
        destructive
        busy={busy}
        onConfirm={confirmAndDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
